/**
 * LocateStep4Address.tsx
 *
 * For each unique delivery address in the selected purchases, the user chooses:
 *   - Add to an existing home
 *   - Create a new home
 *
 * Once all addresses are resolved, the claim API is called.
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, Home as HomeIcon, PlusCircle, Loader2 } from 'lucide-react';
import { GoogleAddressAutocomplete } from '../../components/address/GoogleAddressAutocomplete';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  BrandSmartLookupPurchase,
  BrandSmartImportResponseData,
  brandsmartImport,
} from '../../services/brandsmart.api';
import { getUserHomes, Home } from '../../services/home.api';

interface HomeResolution {
  createNewHome: boolean;
  targetHomeId?: string | null;
}

interface AddressGroup {
  addressKey: string;           // `${city.toLowerCase()}_${zip}`
  displayLabel: string;         // "Miami Gardens, FL 33056"
  purchases: BrandSmartLookupPurchase[];
}

interface Props {
  purchases: BrandSmartLookupPurchase[];
  selectedIds: string[];
  onBack: () => void;
  onDone: (result: BrandSmartImportResponseData) => void;
}

export default function LocateStep4Address({
  purchases,
  selectedIds,
  onBack,
  onDone,
}: Props) {
  const { t } = useTranslation('brandsmart');
  const [homes, setHomes] = useState<Home[]>([]);
  const [loadingHomes, setLoadingHomes] = useState(true);
  const [resolutions, setResolutions] = useState<Record<string, HomeResolution>>({});
  const [customAddresses, setCustomAddresses] = useState<Record<string, string>>({});
  const [claiming, setClaiming] = useState(false);

  // Build address groups from selected purchases
  const addressGroups: AddressGroup[] = React.useMemo(() => {
    const map = new Map<string, AddressGroup>();
    for (const p of purchases) {
      const key = `${p.deliveryCity.toLowerCase()}_${p.deliveryZip}`;
      if (!map.has(key)) {
        map.set(key, {
          addressKey: key,
          displayLabel: `${p.deliveryCity}, ${p.deliveryState} ${p.deliveryZip}`,
          purchases: [],
        });
      }
      map.get(key)!.purchases.push(p);
    }
    return Array.from(map.values());
  }, [purchases]);

  // Load user homes
  useEffect(() => {
    getUserHomes()
      .then(setHomes)
      .catch(() => toast.error(t('locateStep4.loadHomesError')))
      .finally(() => setLoadingHomes(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setResolution(addressKey: string, resolution: HomeResolution) {
    setResolutions((prev) => ({ ...prev, [addressKey]: resolution }));
  }

  const allResolved = addressGroups.every((g) => resolutions[g.addressKey]);

  async function handleClaim() {
    setClaiming(true);
    try {
      const selected = purchases.filter((p) => selectedIds.includes(p.id));
      const imports = selected.map((p) => {
        const key = `${p.deliveryCity.toLowerCase()}_${p.deliveryZip}`;
        const r = resolutions[key]!;
        return {
          purchaseId: p.id,
          targetHomeId: r.createNewHome ? null : (r.targetHomeId ?? null),
          createNewHome: r.createNewHome,
        };
      });
      const result = await brandsmartImport({ imports });
      onDone(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('locateStep4.importError');
      toast.error(msg);
    } finally {
      setClaiming(false);
    }
  }

  if (loadingHomes) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </button>
        <h2 className="text-xl font-semibold mb-1">{t('locateStep4.heading')}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('locateStep4.body')}
        </p>
      </div>

      {addressGroups.map((group) => {
        const resolved = resolutions[group.addressKey];

        return (
          <div key={group.addressKey} className="border rounded-xl p-4 space-y-4">
            {/* Address header */}
            <div>
              <p className="text-xs font-medium uppercase text-muted-foreground tracking-wide">
                {t('locateStep4.deliveryAddress')}
              </p>
              <p className="text-sm font-semibold mt-0.5">{group.displayLabel}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('locateStep4.itemsLabel', { count: group.purchases.length, names: group.purchases.map((p) => p.productName).join(', ') })}
              </p>
            </div>

            {/* Home options */}
            <div className="space-y-2">
              {homes.map((h) => {
                const isSelected =
                  resolved?.targetHomeId === h.id && !resolved.createNewHome;
                return (
                  <button
                    key={h.id}
                    onClick={() =>
                      setResolution(group.addressKey, {
                        targetHomeId: h.id,
                        createNewHome: false,
                      })
                    }
                    className={[
                      'w-full text-left flex items-center gap-3 border-2 rounded-lg px-3 py-2.5 text-sm transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/40',
                    ].join(' ')}
                  >
                    <HomeIcon className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="min-w-0">
                      <p className={`font-medium truncate ${isSelected ? 'text-primary' : ''}`}>
                        {h.name || h.address}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {h.address}, {h.city}, {h.state} {h.zip}
                      </p>
                    </div>
                    {isSelected && (
                      <svg className="h-4 w-4 text-primary ml-auto flex-shrink-0" fill="none" viewBox="0 0 16 16">
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                        <circle cx="8" cy="8" r="3.5" fill="currentColor" />
                      </svg>
                    )}
                  </button>
                );
              })}

              {/* Create new home option */}
              <button
                onClick={() =>
                  setResolution(group.addressKey, {
                    targetHomeId: null,
                    createNewHome: true,
                  })
                }
                className={[
                  'w-full text-left flex items-center gap-3 border-2 rounded-lg px-3 py-2.5 text-sm transition-colors',
                  resolved?.createNewHome
                    ? 'border-primary bg-primary/5'
                    : 'border-dashed border-border hover:border-muted-foreground/40',
                ].join(' ')}
              >
                <PlusCircle
                  className={`h-4 w-4 flex-shrink-0 ${resolved?.createNewHome ? 'text-primary' : 'text-muted-foreground'}`}
                />
                <span className={resolved?.createNewHome ? 'text-primary font-medium' : 'text-muted-foreground'}>
                  {t('locateStep4.createNewHome', { label: group.displayLabel })}
                </span>
                {resolved?.createNewHome && (
                  <svg className="h-4 w-4 text-primary ml-auto flex-shrink-0" fill="none" viewBox="0 0 16 16">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                    <circle cx="8" cy="8" r="3.5" fill="currentColor" />
                  </svg>
                )}
              </button>
              {resolved?.createNewHome && (
                <div className="pt-2">
                  <GoogleAddressAutocomplete
                    value={customAddresses[group.addressKey] || group.displayLabel}
                    onChange={(val) => setCustomAddresses((prev) => ({ ...prev, [group.addressKey]: val }))}
                    onPlaceSelected={(details) => {
                      setCustomAddresses((prev) => ({ ...prev, [group.addressKey]: details.streetAddress }));
                    }}
                    placeholder={group.displayLabel}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      <button
        onClick={handleClaim}
        disabled={!allResolved || claiming}
        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-3 text-sm font-medium disabled:opacity-50 transition-opacity"
      >
        {claiming ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {t('locateStep4.addingInventory')}</>
        ) : (
          t('locateStep4.addToInventory')
        )}
      </button>
    </div>
  );
}
